import {
  createApiRef,
  DiscoveryApi,
  IdentityApi,
} from '@backstage/core-plugin-api';
import {
  ArgusErrorEvent,
  ArgusSourceList, BatchJobResponse,
  ErrorQuery,
  EventResponse, RegisterEventResponse,
  SourcesQuery,
} from '../types';
import { ResponseError } from '@backstage/errors';

export const argusApiRef = createApiRef<ArgusApi>({
  id: 'plugin.argusapi.service',
});

export interface FetchErrorsResponse {
  data: ArgusErrorEvent[];
}

export interface ArgusApi {
  fetchErrors({query, getAllErrors}: FetchErrorsParams): Promise<EventResponse>;

  lockError({
              errorId,
              eventSourceId,
              eventDate,
              currentEnvironment,
            }: LockErrorParams): Promise<Boolean>;

  fetchSources(query: SourcesQuery): Promise<ArgusSourceList>;

  deleteError({
                errorId,
                eventSourceId,
                eventDate,
                currentEnvironment,
              }: DeleteErrorParams): Promise<Boolean>;

  resubmitError({
                  errorId,
                  currentEnvironment,
                }: ResubmitErrorParams): Promise<Boolean>;

  batchDeleteErrors({
                      query,
                      eventSourceId,
                    }: BatchOperationParams): Promise<Boolean>;

  batchResubmitErrors({
                        query,
                        eventSourceId,
                      }: BatchOperationParams): Promise<Boolean>;

  validateEventSourceARNSlackChannel({
                                       eventSourceArn,
                                       slackChannel,
                                       currentEnvironment
                                    }: ValidateEventSourceARNSlackChannelParams): Promise<RegisterEventResponse>;
}

type FetchErrorsParams = {
  query: ErrorQuery
  getAllErrors: Boolean
}

type LockErrorParams = {
  errorId: string;
  eventSourceId: string;
  eventDate: string;
  currentEnvironment: string;
};

type DeleteErrorParams = {
  errorId: string;
  eventSourceId: string;
  eventDate: string;
  currentEnvironment: string;
};

type ResubmitErrorParams = {
  errorId: string;
  currentEnvironment: string;
};

type BatchOperationParams = {
  query: ErrorQuery
  eventSourceId: string;
}

type ValidateEventSourceARNSlackChannelParams = {
    eventSourceArn: string;
    slackChannel: string;
    currentEnvironment: string;
}

type Options = {
  discoveryApi: DiscoveryApi;
  identityApi: IdentityApi;
};

export class ArgusApiClient implements ArgusApi {
  private readonly discoveryApi: DiscoveryApi;
  private readonly identityApi: IdentityApi;

  constructor(options: Options) {
    this.discoveryApi = options.discoveryApi;
    this.identityApi = options.identityApi;
  }

  async fetchErrors({query, getAllErrors}: FetchErrorsParams): Promise<EventResponse> {
    const additionalQueryParams = this.buildAdditionalQueryParams(query);
    const baseUri = this.getDomainUri(query.currentEnvironment);
    const pageSize = getAllErrors ? 99999 : 25
    const apiResponse = this.callApi(
        'GET',
        baseUri,
        `/events?startDt=${query.startDate.toISOString()}&endDt=${query.endDate.toISOString()}&pageSize=${
            query.pageSize ? query.pageSize : pageSize}${additionalQueryParams}`,
    );
    return (await apiResponse).json();
  }


  async fetchSources(query: SourcesQuery): Promise<ArgusSourceList> {
    const baseUri = this.getDomainUri(query.currentEnvironment);
    const apiResponse = this.callApi('GET', baseUri, `/events/event-sources`);
    return (await apiResponse).json();
  }

  async lockError({
                    errorId,
                    eventSourceId,
                    eventDate,
                    currentEnvironment,
                  }: LockErrorParams): Promise<Boolean> {
    const baseUri = this.getDomainUri(currentEnvironment);
    const encodedSourceId = encodeURIComponent(eventSourceId);
    const apiResponse = await this.callApi(
        'PUT',
        baseUri,
        `/events/${errorId}/lock`,
    );
    return (await apiResponse).status === 200;
  }

  async deleteError({
                      errorId,
                      eventSourceId,
                      eventDate,
                      currentEnvironment,
                    }: DeleteErrorParams): Promise<Boolean> {
    const baseUri = this.getDomainUri(currentEnvironment);
    const encodedSourceId = encodeURIComponent(eventSourceId);
    const apiResponse = this.callApi(
        'DELETE',
        baseUri,
        `/events/${errorId}/delete`,
    );
    return (await apiResponse).status === 200;
  }

  async resubmitError({
                        errorId,
                        currentEnvironment,
                      }: ResubmitErrorParams): Promise<Boolean> {
    const baseUri = this.getDomainUri(currentEnvironment);
    const apiResponse = this.callApi(
        'POST',
        baseUri,
        `/events/${errorId}`,
    );
    return (await apiResponse).status === 200;
  }

  async batchDeleteErrors({query, eventSourceId}: BatchOperationParams): Promise<Boolean> {
    const queryBody = {
      startDate: query.startDate,
      endDate: query.endDate,
      eventSourceType: query.eventSource,
      eventSourceId: eventSourceId
    }
    const baseUri = this.getDomainUri(query.currentEnvironment);
    try {
      const apiResponse = await this.callApi(
          'POST',
          baseUri,
          `/events/batchDelete`,
          queryBody,
      );

      if (apiResponse.status === 201) {
        const locationHeader = apiResponse.headers.get("Location");
        if(locationHeader) {
          const batchJobId = locationHeader.substring(locationHeader.lastIndexOf("/") + 1);
          await this.pollForBatchJobCompletion(batchJobId, query.currentEnvironment);
        } else {
          throw new Error("Location header missing in response")
        }
      }

      return apiResponse.status === 200 || apiResponse.status === 201;
    }
    catch (e) {
      throw new Error("Cannot perform mass deletion of unknown source type")
    }
  }

  async batchResubmitErrors({query, eventSourceId}: BatchOperationParams): Promise<Boolean> {
    const queryBody = {
      startDate: query.startDate,
      endDate: query.endDate,
      eventSourceType: query.eventSource,
      eventSourceId: eventSourceId
    }
    const baseUri = this.getDomainUri(query.currentEnvironment);
    try {
      const apiResponse = await this.callApi(
          'POST',
          baseUri,
          `/events/batchResubmit`,
          queryBody,
      );

      if (apiResponse.status === 201) {
        const locationHeader = apiResponse.headers.get("Location");
        if(locationHeader) {
          const batchJobId = locationHeader.substring(locationHeader.lastIndexOf("/") + 1);
          await this.pollForBatchJobCompletion(batchJobId, query.currentEnvironment);
        } else {
          throw new Error("Location header missing in response")
        }
      }

      return apiResponse.status === 200 || apiResponse.status === 201;
    }
    catch (e) {
      throw new Error("Cannot perform mass resubmission of unknown source type")
    }
  }

  async  pollForBatchJobCompletion(batchJobId: string, environment: string) {
    let retries = 300;
    const batchJobResponse = await this.getBatchJobStatus(batchJobId, environment);

    while (batchJobResponse.jobType !== "COMPLETED" && retries > 0) {
      await new Promise(r => setTimeout(r, 1000)); // wait 1 second
      retries--;
    }

    if (retries === 0) {
      throw new Error("Batch job did not complete")
    }
  }

  async getBatchJobStatus(batchJobId: string, currentEnvironment: string): Promise<BatchJobResponse> {
    const baseUri = this.getDomainUri(currentEnvironment);
    const apiResponse = await this.callApi('GET', baseUri, `/events/batchJob/${batchJobId}`);
    return apiResponse.json();
  }

  async validateEventSourceARNSlackChannel(param: ValidateEventSourceARNSlackChannelParams): Promise<RegisterEventResponse> {
    const {eventSourceArn, slackChannel, currentEnvironment} = param;
    const baseUri = this.getDomainUri(currentEnvironment);

    const apiResponse = await this.callApi('POST', baseUri, `/events/register-event-sources`, {eventSourceArn, slackChannel});
    if (apiResponse.status === 200) {
        return {success: true, message: 'Event source registered successfully'};
    }
    return {success: false, message: await apiResponse.text()};
  }

  private async callApi(
      fetchMethod: string,
      baseUri: string,
      path: string,
      body: any = undefined,
  ): Promise<Response> {
    const proxyUri = await this.getApiUrl();
    // for dev right now, we want to go directly to this API
    // rather than go through the backstage proxy because *prod* backstage proxy
    // is incompatible with *nonprod* ISTIO reverse proxy sitting in front of this API
    // in production (when Argus is fully deployed), we want to return this to using
    // the backstage proxy
    const uri = `${baseUri}${path}`;

    const res = await fetch(encodeURI(uri), {
      method: fetchMethod,
      headers: {
        'Content-Type': 'application/json',
      },
      redirect: 'follow',
      credentials: 'include',
      body: JSON.stringify(body),
    });
    if (!res.ok) {
      console.log(res.json());
      throw await ResponseError.fromResponse(res);
    }
    return res;
  }

  private async getApiUrl() {
    const proxyUrl = await this.discoveryApi.getBaseUrl('proxy');
    return `${proxyUrl}/argus-api`;
  }

  private buildAdditionalQueryParams(query: ErrorQuery) {
    let queryString = '';
    if (query.nextPagePointer) {
      queryString += `&nextPageCursor=${query.nextPagePointer}`;
    }
    if (query.eventSource && query.eventSource !== 'ALL SOURCES') {
      queryString += `&eventSourceId=${query.eventSource}`;
    }
    return queryString;
  }

  private getDomainUri(environment: string): string {
    const uri = `https://error-dashboard-api.cwng-qa.imaginelearning.engineering/api`;
    switch (environment) {
      case 'dev':
        return `https://error-dashboard-api.cwng-dev.imaginelearning.engineering/api`;
      case 'qa':
        return `https://error-dashboard-api.cwng-qa.imaginelearning.engineering/api`;
      case 'staging':
        return `https://error-dashboard-api.cwng-staging.imaginelearning.engineering/api`;
      case 'prod':
        return `https://error-dashboard-api.cwng.imaginelearning.engineering/api`;
      case 'local':
        return 'http://localhost:8080/api'
      default:
        return '';
    }
  }
}